import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, MailIcon } from 'lucide-react';
import cvData from '../../data/cv';
import { periodSortKey, condenseRole } from '../cv/helpers';
import { SectionHeading } from './ExperienceTimeline';
export function EarlierAndFooter({
  onViewCv
}: {
  onViewCv?: () => void;
}) {
  const {
    experience,
    earlierExperience,
    education,
    profile,
    meta
  } = cvData;
  const roles = [...experience].sort((a, b) => periodSortKey(b.period) - periodSortKey(a.period));
  const condensed = [...roles.slice(meta.detailedRoleCount).map(condenseRole), ...earlierExperience];
  return <>
      {/* Earlier experience — a compact chronological rail */}
      <section id="earlier" className="mx-auto max-w-4xl px-6 py-20 md:px-12" data-id="element-218" data-mp="3qhpdu9mfxtqw">
        <SectionHeading index={meta.sections.earlier.index} title={meta.sections.earlier.title} data-id="element-219" data-mp="2a4iefhvynxdc" />

        <ol className="relative mt-12" data-id="element-220" data-mp="2f4ejzlkb3wv0">
          {/* continuous rail */}
          <span aria-hidden="true" className="absolute bottom-2 left-[7px] top-2 w-px bg-white/10" data-id="element-221" data-mp="1wa9z71658dod" />

          {condensed.map((r, i) => <motion.li key={i} initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-30px'
        }} transition={{
          duration: 0.4,
          delay: i % 6 * 0.04
        }} className="group/e relative grid grid-cols-[auto_1fr] gap-x-5 pb-7 last:pb-0 sm:grid-cols-[auto_88px_1fr] sm:gap-x-6" data-id="element-222" data-mp="1rdt9i2japr96" data-mpi={i}>
              {/* node */}
              <span className="relative z-10 mt-[5px] flex h-[15px] w-[15px] items-center justify-center" data-id="element-223" data-mp="2xzdp49jdgg8d">
                <span className="h-[7px] w-[7px] rounded-full border border-white/25 bg-[var(--pf-bg)] transition-colors duration-300 [@media(hover:hover)]:group-hover/e:border-[var(--accent)] [@media(hover:hover)]:group-hover/e:bg-[var(--accent)]" data-id="element-224" data-mp="2t1lkqg1jj1na" />
              </span>

              {/* year — its own column on desktop, inline label on mobile */}
              <span className="col-start-2 row-start-1 hidden pt-[2px] font-mono text-[10px] tabular-nums leading-snug text-stone-500 sm:block" data-id="element-225" data-mp="1zlzq54qp4h1x">
                {r.period}
              </span>

              {/* content */}
              <div className="col-start-2 min-w-0 sm:col-start-3" data-id="element-226" data-mp="2ecqg078q962e">
                <span className="mb-1 block font-mono text-[9.5px] tabular-nums text-stone-600 sm:hidden" data-id="element-227" data-mp="2eb5okqhpm38r">
                  {r.period}
                </span>
                <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5" data-id="element-228" data-mp="2ndwxaij7akwp">
                  <h3 className="font-display text-[18px] font-medium leading-tight text-white transition-colors duration-300 [@media(hover:hover)]:group-hover/e:text-[var(--accent)]" data-id="element-229" data-mp="o12m48k3ocy1">
                    {r.company}
                  </h3>
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[var(--accent)]" data-id="element-230" data-mp="lhphigs489pl">
                    {r.role}
                  </span>
                </div>
                {r.note && <p className="mt-1.5 max-w-xl text-[13px] leading-relaxed text-stone-400" data-id="element-231" data-mp="bo8o3dvw4wmg">
                    {r.note}
                  </p>}
              </div>
            </motion.li>)}
        </ol>
      </section>

      {/* Education */}
      <section id="education" className="mx-auto max-w-6xl px-6 py-20 md:px-12" data-id="element-232" data-mp="zgxhvd00lnvd">
        <SectionHeading index={meta.sections.education.index} title={meta.sections.education.title} data-id="element-233" data-mp="ug1incqlypwa" />
        <div className="mt-10 grid gap-8 md:grid-cols-3" data-id="element-234" data-mp="qeoagw01mam7">
          {education.map((edu, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-id="element-235" data-mp="3f8lef7b49edn" data-mpi={i}>
              {edu.period && <span className="font-mono text-[10px] tabular-nums text-stone-600" data-id="element-236" data-mp="1ln8dadquvl91">
                  {edu.period}
                </span>}
              <h3 className="mt-1.5 font-display text-[18px] font-medium leading-snug text-white" data-id="element-237" data-mp="1qlt6ht7ox4nz">
                {edu.degree}
              </h3>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--accent)]" data-id="element-238" data-mp="1gnvh32e3h1ts">
                {edu.school}
              </p>
              {edu.note && <p className="mt-2 text-[12px] text-stone-400" data-id="element-239" data-mp="19rbejbslgld">{edu.note}</p>}
            </motion.div>)}
        </div>
      </section>

      {/* Footer CTA */}
      <footer className="relative overflow-hidden px-6 py-32 md:px-12" data-id="element-240" data-mp="3tt0lvv857xaj">
        <div aria-hidden="true" className="pointer-events-none absolute -left-40 bottom-0 h-[480px] w-[480px] rounded-full opacity-[0.14] blur-[120px]" style={{
        background: 'var(--accent)'
      }} data-id="element-241" data-mp="3ouyywz7ljdts" />
        <div className="relative mx-auto max-w-6xl" data-id="element-242" data-mp="3b4dhlexsjby0">
          <motion.h2 initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="font-display text-[clamp(40px,9vw,110px)] font-light leading-[0.9] tracking-tight text-white" data-id="element-243" data-mp="3b4viioveeec9">
            Let's build
            <br data-id="element-244" data-mp="2ndq1w7hmvi0w" />
            <span className="italic text-stone-400" data-id="element-245" data-mp="2ne9m5uuraii3">something.</span>
          </motion.h2>

          <div className="mt-10 flex flex-wrap items-center gap-4" data-id="element-246" data-mp="3d258kvwjzbly">
            <a href={`mailto:${profile.email}`} className="inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-6 py-3 font-mono text-[12px] uppercase tracking-[0.12em] text-white transition-transform hover:scale-[1.02]" data-id="element-247" data-mp="3h40ekdpfid62">
              <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-248" data-mp="2u42jkh409eo5" />
              {profile.email}
            </a>
            <button type="button" onClick={onViewCv} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 font-mono text-[12px] uppercase tracking-[0.12em] text-stone-300 transition-colors hover:border-white/40 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]" data-id="element-249" data-mp="2y5ed2xq02k1m">
              View print CV
              <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-250" data-mp="ncju0vu6qkji" />
            </button>
          </div>

          <p className="mt-16 border-t border-white/10 pt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-stone-600" data-id="element-251" data-mp="27uc7z2b1im15">
            {profile.name} · {new Date().getFullYear()} · {meta.colophon}
          </p>
        </div>
      </footer>
    </>;
}